import {
  alpha,
  buttonClasses,
  Components,
  svgIconClasses,
  Theme,
} from '@mui/material';

export const getMuiButton = (theme: Theme): Components['MuiButton'] => ({
  defaultProps: {
    variant: 'contained',
    disableRipple: true,
  },

  styleOverrides: {
    root: {
      textTransform: 'none',
      fontFeatureSettings: "'case' on, 'cv11' on, 'calt' off",
      boxShadow: 'none',
      gap: theme.spacing(1.5),

      '& svg': {
        color: 'inherit',
      },
    },

    startIcon: {
      marginLeft: 0,
      marginRight: 0,

      /* fix for extra small and extra large button icon sizes */
      '&.MuiButton-iconSizeExtraLarge': {
        '& > *:first-of-type': {
          fontSize: 28,
        },
      },
      '&.MuiButton-iconSizeExtraSmall': {
        '& > *:first-of-type': {
          fontSize: 16,
        },
      },
    },

    endIcon: {
      marginLeft: 0,
      marginRight: 0,

      /* fix for extra small and extra large button icon sizes */
      '&.MuiButton-iconSizeExtraLarge': {
        '& > *:last-of-type': {
          fontSize: 28,
        },
      },
      '&.MuiButton-iconSizeExtraSmall': {
        '& > *:last-of-type': {
          fontSize: 16,
        },
      },
    },

    iconSizeSmall: {
      [`& > .${svgIconClasses.root}`]: {
        fontSize: 20,
      },
    },

    iconSizeMedium: {
      [`& > .${svgIconClasses.root}`]: {
        fontSize: 24,
      },
    },

    iconSizeLarge: {
      [`& > .${svgIconClasses.root}`]: {
        fontSize: 24,
      },
    },

    sizeSmall: {
      height: 32,
      minHeight: 32,
      padding: theme.spacing(0, 2),
      borderRadius: theme.shape.borderRadiusMd,
      fontSize: 14,
      lineHeight: 1.43,
      fontWeight: 500,
      gap: theme.spacing(1),
    },

    sizeMedium: {
      height: 40,
      minHeight: 40,
      padding: theme.spacing(0, 4),
      borderRadius: theme.shape.borderRadiusMd,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },

    sizeLarge: {
      height: 48,
      minHeight: 48,
      padding: theme.spacing(0, 6),
      borderRadius: theme.shape.borderRadius,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },

    contained: {
      '&:hover': {
        boxShadow: 'none',
      },

      '&:active, &:focus': {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.5)}`,
      },

      [`&.${buttonClasses.disabled}`]: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[400],
      },
    },

    containedPrimary: {
      '&:hover': {
        color: theme.palette.primary.contrastText,
      },
    },

    containedInfo: {
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },

    outlined: {
      '&, &:hover, &:active': {
        border: 'none',
      },

      '&&': {
        boxShadow: `0 0 0 2px ${theme.palette.background.default}`,
      },

      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        boxShadow: `0 0 0 2px ${theme.palette.grey[100]}`,
      },

      [`&.${buttonClasses.disabled}`]: {
        border: 'none',
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 2px ${theme.palette.grey[100]}`,
        color: theme.palette.grey[400],
      },
    },

    outlinedPrimary: {
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },

    outlinedInfo: {
      color: theme.palette.info.contrastText,

      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },

    outlinedSecondary: {
      color: theme.palette.secondary.contrastText,

      '&:hover': {
        color: theme.palette.grey[600],
      },
    },

    outlinedError: {
      '&:hover': {
        color: theme.palette.error.dark,
      },
    },

    outlinedWarning: {
      '&:hover': {
        color: theme.palette.warning.dark,
      },
    },

    text: {
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },

      [`&.${buttonClasses.disabled}`]: {
        color: theme.palette.grey[400],
      },

      [`&.${buttonClasses.sizeLarge}`]: {
        padding: theme.spacing(0, 3),
      },
      [`&.${buttonClasses.sizeMedium}`]: {
        padding: theme.spacing(0, 3),
      },
      [`&.${buttonClasses.sizeSmall}`]: {
        padding: theme.spacing(0, 3),
      },
    },

    textPrimary: {
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },

    textInfo: {
      color: theme.palette.info.contrastText,

      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },

    textSecondary: {
      '&, &:hover': {
        color: theme.palette.grey[600],
      },
    },

    textWarning: {
      '&:hover': {
        color: theme.palette.warning.dark,
      },
    },

    textError: {
      '&:hover': {
        color: theme.palette.error.dark,
      },
    },
  },
  variants: [
    {
      props: { size: 'extraSmall', variant: 'text' },
      style: {
        padding: theme.spacing(0, 2),
      },
    },
    {
      props: { size: 'extraLarge', variant: 'text' },
      style: {
        padding: theme.spacing(0, 4),
      },
    },
    {
      props: { size: 'extraSmall' },
      style: {
        height: 24,
        minHeight: 24,
        padding: theme.spacing(0, 2),
        borderRadius: theme.shape.borderRadiusSm,
        fontSize: 14,
        lineHeight: 1.43,
        fontWeight: 500,
      },
    },
    {
      props: { size: 'extraLarge' },
      style: {
        height: 60,
        minHeight: 60,
        padding: theme.spacing(0, 8),
        borderRadius: theme.shape.borderRadiusXl,
        fontSize: 20,
        lineHeight: 1.4,
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
    },
  ],
});
